<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useUiStore, type ToastKind } from '@/stores/ui'

const { t } = useI18n()
const ui = useUiStore()
const { toasts } = storeToRefs(ui)

const GLYPH: Record<ToastKind, string> = {
  success: '✓',
  error: '!',
  warning: '▲',
  info: 'i',
}
</script>

<template>
  <div class="ui-toasts" role="region" :aria-label="t('ui.toast.region')">
    <div
      v-for="toast in toasts"
      :key="toast.id"
      :class="['ui-toast', `ui-toast--${toast.kind}`]"
      :role="toast.kind === 'error' ? 'alert' : 'status'"
      aria-live="polite"
    >
      <span class="ui-toast__glyph" aria-hidden="true">{{ GLYPH[toast.kind] }}</span>
      <div class="ui-toast__content">
        <p class="ui-toast__title">{{ toast.title }}</p>
        <p v-if="toast.description" class="ui-toast__description">{{ toast.description }}</p>
      </div>
      <button
        type="button"
        class="ui-toast__close"
        :aria-label="t('ui.toast.dismiss')"
        @click="ui.dismissToast(toast.id)"
      >
        ✕
      </button>
    </div>
  </div>
</template>

<style scoped>
.ui-toasts {
  position: fixed;
  inset-inline-end: var(--space-5);
  bottom: var(--space-5);
  z-index: 80;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: min(400px, calc(100vw - var(--space-8)));
  pointer-events: none;
}

.ui-toast {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius);
  border: 1px solid var(--border);
  border-inline-start-width: 3px;
  background: var(--surface-2);
  box-shadow: var(--shadow-modal);
  pointer-events: auto;
}

.ui-toast--success {
  border-left-color: var(--status-online);
}

.ui-toast--error {
  border-left-color: var(--status-offline);
}

.ui-toast--warning {
  border-left-color: var(--status-degraded);
}

.ui-toast--info {
  border-left-color: var(--accent);
}

.ui-toast__glyph {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: var(--text-xs);
  font-weight: 700;
}

.ui-toast--success .ui-toast__glyph {
  background: var(--status-online-soft);
  color: var(--status-online-text);
}

.ui-toast--error .ui-toast__glyph {
  background: var(--status-offline-soft);
  color: var(--status-offline-text);
}

.ui-toast--warning .ui-toast__glyph {
  background: var(--status-degraded-soft);
  color: var(--status-degraded-text);
}

.ui-toast--info .ui-toast__glyph {
  background: var(--accent-soft);
  color: var(--accent);
}

.ui-toast__content {
  min-width: 0;
  flex: 1;
}

.ui-toast__title {
  font-size: var(--text-sm);
  font-weight: 600;
}

.ui-toast__description {
  margin-top: 2px;
  font-size: var(--text-xs);
  color: var(--text-secondary);
  word-break: break-word;
}

.ui-toast__close {
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0;
  font-size: var(--text-xs);
  line-height: 1;
}

.ui-toast__close:hover {
  color: var(--text-primary);
}
</style>
